import React, { memo, PropsWithChildren, useMemo, useState } from 'react'
import { Popover, PopoverProps } from 'react-tiny-popover'
import theme from 'theme_'
import { TooltipBlock } from './styles'

type ExtendProps = Omit<
  PopoverProps,
  'isOpen' | 'onClickOutside' | 'reposition' | 'content'
> &
  Partial<Pick<PopoverProps, 'content'>>
interface TooltipProps extends ExtendProps {
  /** Variant of showing popover */
  showOn: 'hover' | 'click'
  /**
   * Alternative to content, just string.
   * Paste text into popover
   */
  text?: string
  fullWidth?: boolean
}

function Tooltip({
  children,
  showOn,
  text,
  fullWidth,
  content: baseContent,
  ...props
}: PropsWithChildren<TooltipProps>) {
  const [isOpen, setIsOpen] = useState(false)

  /**
   * @description hide popover when user clicks outside
   */
  function onClickOutside() {
    setIsOpen(false)
  }

  /**
   * @description if showOn equals hover we need to
   * open popover when mouse over element and hide
   * when mouse leave
   *
   * onClick just toggle state
   */
  const divProps: Partial<React.HTMLAttributes<HTMLDivElement>> =
    useMemo(() => {
      if (showOn === 'hover') {
        return {
          onMouseOver: () => setIsOpen(true),
          onMouseLeave: () => setIsOpen(false),
        }
      }
      return {
        onClick: () => setIsOpen((prev) => !prev),
      }
    }, [showOn, setIsOpen])

  /**
   * @description TooltipBlock needs to add some styles
   * to modal and don't block listeners and styles inside
   *
   * text renders a string which is passed in props
   */
  const content = useMemo(() => {
    if (baseContent) {
      if (baseContent instanceof Function) {
        return ((i) => (
          <TooltipBlock>{baseContent(i)}</TooltipBlock>
        )) as PopoverProps['content']
      } else {
        return <TooltipBlock $fullWidth={fullWidth}>{baseContent}</TooltipBlock>
      }
    } else if (text) {
      return <TooltipBlock>{text}</TooltipBlock>
    }
    return <></>
  }, [baseContent, fullWidth, text])

  return (
    <Popover
      {...props}
      isOpen={isOpen}
      content={content}
      onClickOutside={onClickOutside}
      reposition
      containerStyle={{ zIndex: theme.zIndex.tooltip }}
    >
      <div {...divProps}>{children}</div>
    </Popover>
  )
}

export default memo(Tooltip)
